// 프로젝트 상태 (7단계)
export const PROJECT_STATUSES = ['draft', 'waiting', 'planning', 'in_progress', 'completed', 'closed', 'cancelled'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: '기안',
  waiting: '대기',
  planning: '계획',
  in_progress: '진행중',
  completed: '완료',
  closed: '최종',
  cancelled: '취소',
};

// 읽기 전용 프로젝트 상태 (수정 불가)
export const READONLY_PROJECT_STATUSES: ProjectStatus[] = ['closed', 'cancelled'];

// 기본 목록에서 숨기는 프로젝트 상태(완료/최종/취소). 명시적으로 선택하면 조회 가능.
export const DEFAULT_HIDDEN_PROJECT_STATUSES: ProjectStatus[] = ['completed', 'closed', 'cancelled'];

// 항상 숨기는 프로젝트 상태(최종/취소). 토글과 무관하게 목록에서 제외.
export const ALWAYS_HIDDEN_PROJECT_STATUSES: ProjectStatus[] = ['closed', 'cancelled'];

// 프로젝트 중요도 (A=최고 중요/중대 → F=최저/사소). 신규 속성 기본값 D.
export const IMPORTANCE_LEVELS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
export type ImportanceLevel = (typeof IMPORTANCE_LEVELS)[number];
export const DEFAULT_IMPORTANCE: ImportanceLevel = 'D';
export const IMPORTANCE_LABELS: Record<ImportanceLevel, string> = {
  A: 'A · 최상',
  B: 'B · 상',
  C: 'C · 중상',
  D: 'D · 중',
  E: 'E · 하',
  F: 'F · 최하',
};

// 안건 상태 (4단계)
export const AGENDA_STATUSES = ['draft', 'discussion', 'completed', 'closed'] as const;
export type AgendaStatus = (typeof AGENDA_STATUSES)[number];

export const AGENDA_STATUS_LABELS: Record<AgendaStatus, string> = {
  draft: '기안',
  discussion: '협의',
  completed: '완료',
  closed: '종결',
};

// 공지사항 상태 (2단계)
export const NOTICE_STATUSES = ['draft', 'published'] as const;
export type NoticeStatus = (typeof NOTICE_STATUSES)[number];

export const NOTICE_STATUS_LABELS: Record<NoticeStatus, string> = {
  draft: '임시저장',
  published: '배포완료',
};

// 이슈 리포트 상태 (5단계)
export const ISSUE_REPORT_STATUSES = ['draft', 'approved', 'in_progress', 'completed', 'closed'] as const;
export type IssueReportStatus = (typeof ISSUE_REPORT_STATUSES)[number];

export const ISSUE_REPORT_STATUS_LABELS: Record<IssueReportStatus, string> = {
  draft: '기안',
  approved: '승인',
  in_progress: '진행',
  completed: '완료',
  closed: '종결',
};

// 이슈 리포트 유형
export const ISSUE_REPORT_TYPES = ['bug', 'improvement'] as const;
export type IssueReportType = (typeof ISSUE_REPORT_TYPES)[number];

export const ISSUE_REPORT_TYPE_LABELS: Record<IssueReportType, string> = {
  bug: '버그',
  improvement: '개선사항',
};

// 프로젝트 등록 요청 상태
export const PROJECT_REQUEST_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type ProjectRequestStatus = (typeof PROJECT_REQUEST_STATUSES)[number];

export const PROJECT_REQUEST_STATUS_LABELS: Record<ProjectRequestStatus, string> = {
  pending: '대기',
  approved: '승인',
  rejected: '반려',
};

// 활동 상태 (6단계: 가등록 워크플로우 포함)
export const ACTIVITY_STATUSES = ['provisional', 'pending', 'in_progress', 'completed', 'cancelled', 'rejected'] as const;
export type ActivityStatus = (typeof ACTIVITY_STATUSES)[number];

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  provisional: '가등록',
  pending: '대기',
  in_progress: '진행중',
  completed: '완료',
  cancelled: '변경/중단',
  rejected: '거절',
};

// 정식 업무 상태 (가등록/거절 제외 — 인라인 상태 드롭다운, 대시보드/간트 필터용)
export const APPROVED_ACTIVITY_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'] as const;
export type ApprovedActivityStatus = (typeof APPROVED_ACTIVITY_STATUSES)[number];

// 작성자가 본인 생성 업무를 직접 soft-delete 할 수 있는 상태 (활성/완료 제외)
export const CREATOR_DELETABLE_STATUSES = ['provisional', 'pending', 'rejected', 'cancelled'] as const;
export type CreatorDeletableStatus = (typeof CREATOR_DELETABLE_STATUSES)[number];

// 역할
export const ROLE_TYPES = ['master', 'admin', 'user', 'pending'] as const;
export type RoleType = (typeof ROLE_TYPES)[number];

export const ROLE_LABELS: Record<RoleType, string> = {
  master: '마스터',
  admin: '관리자',
  user: '사용자',
  pending: '승인대기',
};

// 노력수준 (5단계, 1.0 ≈ 8시간 기준)
export const EFFORT_LEVELS = {
  very_low: { name: '매우낮음', weight: 0.05, color: 'gray', sortOrder: 1 },
  low: { name: '낮음', weight: 0.1, color: 'blue', sortOrder: 2 },
  normal: { name: '보통', weight: 0.25, color: 'green', sortOrder: 3 },
  high: { name: '높음', weight: 0.4, color: 'orange', sortOrder: 4 },
  very_high: { name: '매우높음', weight: 0.6, color: 'red', sortOrder: 5 },
} as const;

export const EFFORT_LEVEL_CODES = ['very_low', 'low', 'normal', 'high', 'very_high'] as const;
export type EffortLevelCode = (typeof EFFORT_LEVEL_CODES)[number];

// 워크로드 범위
export const WORKLOAD_THRESHOLDS = {
  low: 0.3,      // 여유
  normal: 0.6,   // 보통
  high: 1.0,     // 높음
  // > 1.0 = 과적
} as const;

// 직책
export const RANK_TYPES = ['임원', '팀장', '팀원'] as const;
export type RankType = (typeof RANK_TYPES)[number];

// 감사 로그 액션
export const AUDIT_ACTIONS = [
  'CREATE', 'UPDATE', 'DELETE', 'STATUS_CHANGE', 'EFFORT_CHANGE',
  'LOGIN', 'LOGOUT', 'REGISTER', 'APPROVE_USER', 'REJECT_USER', 'PASSWORD_RESET', 'TOGGLE_ACTIVE',
  'APPROVE_ACTIVITY', 'REJECT_ACTIVITY',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: '생성',
  UPDATE: '수정',
  DELETE: '삭제',
  STATUS_CHANGE: '상태변경',
  EFFORT_CHANGE: '공수변경',
  LOGIN: '로그인',
  LOGOUT: '로그아웃',
  REGISTER: '회원가입',
  APPROVE_USER: '사용자승인',
  REJECT_USER: '가입거부',
  PASSWORD_RESET: '비밀번호초기화',
  TOGGLE_ACTIVE: '활성화토글',
  APPROVE_ACTIVITY: '업무승인',
  REJECT_ACTIVITY: '업무거절',
};

export const AUDIT_ENTITY_TYPE_LABELS: Record<AuditEntityType, string> = {
  project: '프로젝트',
  activity: '업무',
  user: '사용자',
  client: '고객사',
  client_contact: '고객담당자',
  department: '부서코드',
  user_department: '사내부서',
  classification: '프로젝트분류',
  category: '업무카테고리',
  subcategory: '세부카테고리',
  effort_level: '공수레벨',
  deliverable: '산출물',
  notification: '알림',
  agenda: '안건',
  meeting_session: '회의차수',
  issue_report: '이슈리포트',
  notice: '공지사항',
  project_request: '프로젝트 등록요청',
};

// 감사 로그 엔티티 타입
export const AUDIT_ENTITY_TYPES = [
  'project', 'activity', 'user', 'client', 'client_contact',
  'department', 'user_department', 'classification', 'category',
  'subcategory', 'effort_level', 'deliverable', 'notification',
  'agenda', 'meeting_session', 'issue_report', 'notice', 'project_request',
] as const;
export type AuditEntityType = (typeof AUDIT_ENTITY_TYPES)[number];

// 감사 로그 필드 한글 라벨
export const AUDIT_FIELD_LABELS: Record<string, string> = {
  name: '이름',
  status: '상태',
  start_date: '시작일',
  startDate: '시작일',
  end_date: '종료일',
  endDate: '종료일',
  target_date: '목표일',
  targetDate: '목표일',
  progress: '진행률',
  effort_level: '공수',
  description: '개요',
  content: '내용',
  notes: '비고',
  plan_description: '업무계획',
  planDescription: '업무계획',
  result_description: '결과',
  resultDescription: '결과',
  email: '이메일',
  position: '직급',
  rank: '직위',
  role_id: '역할',
  department_id: '부서',
  departmentId: '부서',
  client_id: '고객사',
  clientId: '고객사',
  classification_id: '분류',
  classificationId: '분류',
  pm_id: 'PM',
  pmId: 'PM',
  is_urgent: '긴급',
  isUrgent: '긴급',
  is_active: '활성화',
  isActive: '활성화',
  estimated_amount: '추정금액',
  nas_path: 'NAS경로',
  projectId: '프로젝트',
  title: '제목',
  username: '아이디',
  restored: '복원',
  assignee_added: '담당자 추가',
  assignee_removed: '담당자 제거',
  close_comment: '종결사유',
  closeComment: '종결사유',
  drafted_at: '기안일',
  draftedAt: '기안일',
  discussion_at: '협의일',
  discussionAt: '협의일',
  completed_at: '완료일',
  completedAt: '완료일',
  session_number: '회의차수',
  sessionNumber: '회의차수',
  meeting_date: '회의일',
  meetingDate: '회의일',
  summary: '안건',
  result: '회의 내용',
  decision: '결정 사항',
  email_sent: '이메일 발송',
  emailSent: '이메일 발송',
  attendees: '참석자',
  rejection_reason: '거절사유',
  rejectionReason: '거절사유',
};

// 알림 타입
export const NOTIFICATION_TYPES = ['task_assigned', 'deadline_approaching', 'project_status_changed', 'urgent_changed', 'schedule_changed', 'system', 'meeting_called', 'agenda_status_changed', 'activity_approved', 'activity_rejected', 'issue_report_created', 'issue_report_status_changed', 'issue_report_comment', 'notice_published', 'project_request_created', 'project_request_approved', 'project_request_rejected', 'project_assigned'] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

// 토큰 만료 시간
export const TOKEN_EXPIRY = {
  accessToken: 30 * 60 * 1000,          // 30분
  refreshToken: 7 * 24 * 60 * 60 * 1000, // 7일
  graceperiod: 10 * 1000,               // 10초 (멀티탭 refresh 경합 방지)
} as const;

// Rate Limit
export const RATE_LIMITS = {
  login: { max: 10, windowMs: 60 * 1000 },
  register: { max: 5, windowMs: 60 * 60 * 1000 },
  refresh: { max: 30, windowMs: 60 * 1000 },
  admin: { max: 20, windowMs: 60 * 1000 },
  general: { max: 300, windowMs: 60 * 1000 },
  // 외부 연동 데이터 API — username 버킷 (같은 IP의 사용자 트래픽과 경합 방지)
  integration: { max: 120, windowMs: 60 * 1000 },
} as const;

// 페이지네이션
export const PAGINATION = {
  defaultPage: 1,
  defaultLimit: 20,
  maxLimit: 100,
} as const;

// UI 디바운스
export const DEBOUNCE = {
  inlineEdit: 500,
  filter: 300,
} as const;

// 프로젝트 분류 기본값
export const DEFAULT_CLASSIFICATIONS = [
  { code: 'P', name: '고객사별프로젝트' },
  { code: 'D', name: '내부개발' },
  { code: 'R', name: '국책과제' },
  { code: 'M', name: '유지보수' },
  { code: 'A', name: 'AS' },
] as const;

// === 스토리보드 (일일 업무 기록 / 타임시트) ===

// 구분 (행별, work_location)
export const WORK_LOCATIONS = ['office', 'field'] as const;
export type WorkLocation = (typeof WORK_LOCATIONS)[number];

export const WORK_LOCATION_LABELS: Record<WorkLocation, string> = {
  office: '내근',
  field: '외근',
};

// Activity 근무 형태 (activities.work_mode) — NULL(미지정) 허용.
// worklog_entries.work_location(내근/외근, 행 단위)과 값 이름을 맞추되 'trip'(출장)이 하나 더 있다.
// 분류(category)와 직교한 축이다 — 출장 가서 한 SW개발은 분류 'SW개발' + work_mode 'trip'.
export const ACTIVITY_WORK_MODES = ['office', 'field', 'trip'] as const;
export type ActivityWorkMode = (typeof ACTIVITY_WORK_MODES)[number];

export const ACTIVITY_WORK_MODE_LABELS: Record<ActivityWorkMode, string> = {
  office: '내근',
  field: '외근',
  trip: '출장',
};

// 당일 근무유형 (일별, day_type) — 시간 합산 참고용
export const DAY_TYPES = ['full', 'half', 'quarter', 'annual'] as const;
export type DayType = (typeof DAY_TYPES)[number];

export const DAY_TYPE_LABELS: Record<DayType, string> = {
  full: '전일',
  half: '반차',
  quarter: '반반차',
  annual: '연차',
};

// day_type별 Load 가중치 — 마스터 집계(가중일수) 계산용.
// ※ worklog_days 레코드가 없는 영업일은 full(=1)로 간주한다 (성실 풀근무자의
//    가중일수가 반차/연차 등록자보다 낮아지는 역전 방지). 미존재시 full 적용.
export const DAY_TYPE_LOAD_WEIGHT: Record<DayType, number> = {
  full: 1,
  half: 0.5,
  quarter: 0.25,
  annual: 0,
};

// 투입시간 프리셋 (시간)
export const HOURS_PRESETS = [0.25, 0.5, 1, 2, 4] as const;

// 업무타이틀/업무내용 길이 — 문자 수 단일기준(한글=1). CAP 카운터·DB varchar·Zod max 단일소스.
export const SUMMARY_MAX = 50;
export const CONTENT_MAX = 200;

// 스토리보드 즐겨찾기 최대 개수 (slot 1~10 = Ctrl+1~0 고정 매핑)
export const STORYBOARD_FAVORITE_MAX = 10;

// 다건 이동/불러오기 1회 상한 (bulkApproveSchema의 50과 동일 기준)
export const WORKLOG_BULK_MAX = 50;

// 지난 기록 불러오기 시 강제되는 투입시간 — 반복 업무는 내용만 같고 시간은 매일 다르므로
// 소스 값을 그대로 가져오면 틀린 시간이 조용히 확정된다. 서버가 고정하며 요청 필드로 열지 않는다.
export const WORKLOG_COPY_HOURS = 0.25;
